import * as THREE from 'three';
import { SpriteCloud } from './sprites.js';

const smooth = THREE.MathUtils.smoothstep;

/**
 * A column of smoke from a point: a few streams of puffs rising and drifting
 * off with the wind, growing as they go, one draw call (SpriteCloud). The
 * volcano's plume; a chimney, a campfire or steam in part 2's worlds.
 * `level` (0–1, a feature's level) sets how high it reaches and how thick it
 * is: a wisp when low, gone at 0.
 */
export function smokeColumn(options: {
  map: THREE.Texture;
  colour: THREE.ColorRepresentation;
  opacity?: number;
  name: string;
  from: THREE.Vector3;
  count?: number;
  /** How far the puffs travel before they are gone, at level 0 and at level 1. */
  height: readonly [number, number];
  /** Puff size where it leaves, and how much it grows by the end. */
  size?: readonly [number, number];
  /** Seconds for a puff to rise and fade. */
  period?: number;
  /** Side to side, metres. */
  wander?: number;
  streams?: readonly { drift: THREE.Vector3; offset: THREE.Vector3 }[];
}): { mesh: SpriteCloud['mesh']; update(elapsed: number, level: number): void } {
  const count = options.count ?? 18;
  const [quiet, full] = options.height;
  const [start, grow] = options.size ?? [full * 0.08, full * 0.55];
  const period = options.period ?? 55;
  const wander = options.wander ?? full * 0.03;
  const given = options.streams ?? [
    {
      drift: new THREE.Vector3(0.55, 1.1, -0.3),
      offset: new THREE.Vector3(-0.02, 0, 0.01).multiplyScalar(full),
    },
    {
      drift: new THREE.Vector3(0.75, 0.95, -0.45),
      offset: new THREE.Vector3(0.02, 0, -0.01).multiplyScalar(full),
    },
    { drift: new THREE.Vector3(0.4, 1.2, -0.2), offset: new THREE.Vector3() },
  ];
  // Normalised once, so a frame clones nothing.
  const streams = given.map(({ drift, offset }) => ({ drift: drift.clone().normalize(), offset }));
  const cloud = new SpriteCloud(count, {
    map: options.map,
    colour: options.colour,
    opacity: options.opacity ?? 0.9,
    name: options.name,
  });
  const p = new THREE.Vector3();
  return {
    mesh: cloud.mesh,
    update(elapsed, level) {
      cloud.mesh.visible = level > 0.01;
      if (!cloud.mesh.visible) return;
      for (let i = 0; i < count; i++) {
        const stream = streams[i % streams.length]!;
        const u = (((elapsed / period + i / count) % 1) + 1) % 1;
        p.copy(options.from)
          .add(stream.offset)
          .addScaledVector(stream.drift, u * (quiet + (full - quiet) * level));
        p.x += Math.sin(u * 6 + i) * wander;
        const alpha = smooth(u, 0, 0.08) * (1 - smooth(u, 0.45, 1)) * Math.min(1, level * 1.3);
        cloud.set(i, p, (start + u * grow) * (0.5 + 0.5 * level), alpha, i * 1.7 + u * 0.8);
      }
      cloud.commit();
    },
  };
}
